import { useEffect, useRef, useState } from "react";
import Spinner from "@/components/Spinner";
import { X, Camera, Sparkles, Check, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * NutritionLabelContributor — the "help complete this product" flow.
 *
 * Presented to the user when they scan a barcode that OpenFoodFacts has
 * indexed but with incomplete macros (very common for Gulf-region
 * products where OFF's crowdsourced completeness sits around 30-40%).
 *
 * 3-step flow:
 *  1) intro          — value-prop card + "capture label" primary CTA
 *  2) analyzing      — full-screen loader while GPT-4o Vision reads label
 *  3) review         — pre-filled per-100g form the user can correct
 *                      (submit → /foods/contribute → local save + OFF push)
 *
 * Emits `onContributed(food)` once the local save succeeds so the parent
 * (AddMeal.jsx) can drop the fresh food straight into the meal review UI.
 */
export default function NutritionLabelContributor({
  barcode,
  suggestedName = "",
  isLiquid: initialIsLiquid = false,
  onContributed,
  onClose,
  onSkipManual,
}) {
  const { lang } = useI18n();
  const isEn = lang === "en";
  const fileRef = useRef(null);
  const [step, setStep] = useState("intro"); // intro | analyzing | review | saving
  const [form, setForm] = useState({
    name_ar: suggestedName || "",
    name_en: "",
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fat_g: 0,
    sugar_g: 0,
    fiber_g: 0,
    salt_g: 0,
    saturated_fat_g: 0,
    serving_size_g: 0,
    package_size_g: 0,
    is_liquid: initialIsLiquid,
  });
  const [ocrConfidence, setOcrConfidence] = useState(null);

  const openCamera = () => fileRef.current?.click();

  // Auto-open camera the first time the sheet appears so the user
  // doesn't need an extra tap.
  useEffect(() => {
    if (step !== "intro") return;
    // Small delay so the sheet animates in before the OS camera modal
    // overlays it — otherwise Android sometimes eats the click.
    const t = setTimeout(openCamera, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyzeLabel = async (file) => {
    setStep("analyzing");
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = () => rej(new Error("read fail"));
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/foods/nutrition-ocr", {
        image_base64: b64,
        barcode,
      });
      const p = data.per_100g || {};
      const next = {
        ...form,
        name_ar: data.product_name_ar || data.product_name_en || form.name_ar,
        name_en: data.product_name_en || form.name_en,
        calories: p.calories || 0,
        protein_g: p.protein_g || 0,
        carbs_g: p.carbs_g || 0,
        fat_g: p.fat_g || 0,
        sugar_g: p.sugar_g || 0,
        fiber_g: p.fiber_g || 0,
        salt_g: p.salt_g || 0,
        saturated_fat_g: p.saturated_fat_g || 0,
        serving_size_g: data.serving_size_g || form.serving_size_g || 0,
        package_size_g: data.package_size_g || form.package_size_g || 0,
        is_liquid: data.is_liquid ?? form.is_liquid,
      };
      if (!next.name_ar.trim()) next.name_ar = isEn ? "Packaged product" : "منتج غذائي";
      setForm(next);
      setOcrConfidence(data.confidence ?? null);
      // Calories read OK → skip the review form and save straight away.
      if ((Number(next.calories) || 0) > 0) {
        await save(next);
        return;
      }
      setStep("review");
      toast.warning(
        isEn
          ? "We couldn't read the calories — please fill them in"
          : "ما قدرنا نقرأ السعرات — عبّيها قبل الحفظ",
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(detail || (isEn ? "Daily AI limit reached" : "وصلت للحد اليومي"));
      } else {
        toast.error(
          isEn
            ? "Couldn't read the label — try a clearer photo"
            : "تعذّرت قراءة الملصق — التقط صورة أوضح",
        );
      }
      setStep("intro");
    }
  };

  const save = async (f = form) => {
    const form = f;
    if (!form.name_ar.trim()) {
      toast.error(isEn ? "Product name is required" : "اسم المنتج مطلوب");
      return;
    }
    if ((Number(form.calories) || 0) <= 0) {
      toast.error(isEn ? "Calories can't be zero" : "لا يمكن أن تكون السعرات صفراً");
      return;
    }
    setStep("saving");
    try {
      const { data } = await api.post("/foods/contribute", {
        barcode,
        name_ar: form.name_ar.trim(),
        name_en: form.name_en?.trim() || null,
        is_liquid: !!form.is_liquid,
        serving_size_g: Number(form.serving_size_g) || 0,
        package_size_g: Number(form.package_size_g) || 0,
        per_100g: {
          calories: Number(form.calories) || 0,
          protein_g: Number(form.protein_g) || 0,
          carbs_g: Number(form.carbs_g) || 0,
          fat_g: Number(form.fat_g) || 0,
        },
        sugar_g: Number(form.sugar_g) || 0,
        fiber_g: Number(form.fiber_g) || 0,
        salt_g: Number(form.salt_g) || 0,
        saturated_fat_g: Number(form.saturated_fat_g) || 0,
        // Always mirror to OpenFoodFacts when credentials are configured on
        // the backend — the option is not surfaced to the user any more.
        push_to_openfoodfacts: true,
      });
      toast.success(isEn ? "Saved ✓" : "تم الحفظ ✓");
      onContributed?.(data.food);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || (isEn ? "Save failed" : "تعذّر الحفظ"),
      );
      setStep("review");
    }
  };

  const setField = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const macroRow = (label, key, unit = "g", step = "0.1") => (
    <div className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-0">
      <div className="flex-1 text-sm">{label}</div>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          value={form[key]}
          onChange={setField(key)}
          className="w-20 bg-neutral-50 border border-neutral-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-500/40"
          data-testid={`contribute-input-${key}`}
        />
        <span className="text-xs text-neutral-400 w-8">{unit}</span>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[110] bg-white flex flex-col"
      dir={isEn ? "ltr" : "rtl"}
      data-testid="nutrition-contributor"
    >
      <header className="px-4 h-14 flex items-center justify-between border-b border-neutral-100 bg-white sticky top-0">
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center"
          data-testid="contribute-close-btn"
          aria-label="close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-sm font-semibold">
          {isEn ? "Read nutrition label" : "قراءة ملصق التغذية"}
        </div>
        <div className="w-9" />
      </header>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && analyzeLabel(e.target.files[0])}
        data-testid="contribute-camera-input"
      />

      {step === "intro" && (
        <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-8 py-6 gap-5 text-center">
          <div className="space-y-2 max-w-sm">
            <h2 className="text-xl font-black">
              {isEn ? "We didn't find this product" : "لم نجد هذا المنتج"}
            </h2>
            <p className="text-sm text-neutral-500 leading-relaxed">
              {isEn
                ? "Snap the nutrition facts table on the pack, like this example 👇"
                : "صوّر جدول القيم الغذائية (الملصق / الليبل) على العبوة، مثل هذا المثال 👇"}
            </p>
          </div>
          <img
            src={isEn ? "/img/label-example-en.jpg" : "/img/label-example-ar.jpg"}
            alt={isEn ? "Nutrition label example" : "مثال على ملصق القيم الغذائية"}
            className="w-44 h-56 object-cover object-top rounded-2xl border border-neutral-200 shadow-md"
            data-testid="contribute-label-example-img"
          />
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            {isEn ? "We'll read the calories & macros in seconds" : "نقرأ لك السعرات والماكرو في ثوانٍ"}
          </div>
          <div className="w-full max-w-xs space-y-3">
            <button
              onClick={openCamera}
              className="w-full orange-gradient text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] shadow-brand"
              data-testid="contribute-open-camera-btn"
            >
              <Camera className="w-5 h-5" />
              {isEn ? "Open camera" : "افتح الكاميرا"}
            </button>
            <button
              onClick={() => (onSkipManual || onClose)?.()}
              className="w-full bg-white border-2 border-neutral-200 text-neutral-800 font-bold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="contribute-skip-manual-btn"
            >
              <Edit3 className="w-4 h-4" />
              {isEn ? "Skip & enter manually" : "تخطّى وأدخل يدوياً"}
            </button>
          </div>
        </div>
      )}

      {(step === "analyzing" || step === "saving") && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8">
          <Spinner size="lg" bare />
          <div className="text-lg font-bold">
            {step === "analyzing"
              ? isEn
                ? "Reading the label..."
                : "جارٍ قراءة الملصق..."
              : isEn
                ? "Saving..."
                : "جارٍ الحفظ..."}
          </div>
          <p className="text-sm text-neutral-500 max-w-xs">
            {step === "analyzing"
              ? isEn
                ? "Extracting calories and macros from the image."
                : "نستخرج السعرات والماكرو من الصورة."
              : isEn
                ? "Just a moment..."
                : "لحظات..."}
          </p>
        </div>
      )}

      {step === "review" && (
        <div className="flex-1 overflow-y-auto p-5 space-y-5 pb-32">
          {ocrConfidence !== null && ocrConfidence < 0.6 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-800">
              {isEn
                ? "AI wasn't sure — please double-check the numbers."
                : "الذكاء الاصطناعي غير متأكد — راجع الأرقام قبل الحفظ."}
            </div>
          )}

          <div>
            <label className="text-xs text-neutral-500 font-medium mb-1 block">
              {isEn ? "Product name (Arabic)" : "اسم المنتج بالعربية"}
            </label>
            <input
              type="text"
              value={form.name_ar}
              onChange={setField("name_ar")}
              placeholder={isEn ? "e.g. Deemah Biscuits" : "مثال: بسكويت ديمة"}
              className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              data-testid="contribute-input-name-ar"
            />
          </div>

          <div>
            <label className="text-xs text-neutral-500 font-medium mb-1 block">
              {isEn ? "Product name (English, optional)" : "الاسم بالإنجليزية (اختياري)"}
            </label>
            <input
              type="text"
              value={form.name_en}
              onChange={setField("name_en")}
              placeholder="Deemah Chocolate Biscuits"
              className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              data-testid="contribute-input-name-en"
              dir="ltr"
            />
          </div>

          <div className="bg-white border border-neutral-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold">
                {isEn ? "Per 100 g / 100 ml" : "لكل 100 غ / 100 مل"}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-neutral-500">
                <input
                  type="checkbox"
                  checked={form.is_liquid}
                  onChange={setField("is_liquid")}
                  data-testid="contribute-is-liquid"
                />
                {isEn ? "Beverage" : "مشروب"}
              </label>
            </div>
            {macroRow(isEn ? "Calories" : "السعرات", "calories", "kcal", "1")}
            {macroRow(isEn ? "Protein" : "بروتين", "protein_g")}
            {macroRow(isEn ? "Carbs" : "كربوهيدرات", "carbs_g")}
            {macroRow(isEn ? "Fat" : "دهون", "fat_g")}
            <div className="pt-2 mt-2 border-t border-dashed border-neutral-200">
              <div className="text-[11px] text-neutral-400 mb-1">
                {isEn ? "Optional micros" : "قيم إضافية (اختياري)"}
              </div>
              {macroRow(isEn ? "Sugar" : "سكر", "sugar_g")}
              {macroRow(isEn ? "Fiber" : "ألياف", "fiber_g")}
              {macroRow(isEn ? "Salt" : "ملح", "salt_g")}
              {macroRow(isEn ? "Saturated fat" : "دهون مشبعة", "saturated_fat_g")}
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-500 font-medium mb-1 block">
              {isEn ? "Pack size (whole bottle / box)" : "حجم العبوة كاملة"}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={form.package_size_g}
                onChange={setField("package_size_g")}
                className="flex-1 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                data-testid="contribute-input-package"
              />
              <span className="text-sm text-neutral-500">
                {form.is_liquid ? (isEn ? "ml" : "مل") : (isEn ? "g" : "غ")}
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-500 font-medium mb-1 block">
              {isEn ? "Serving size" : "حجم الحصة"}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={form.serving_size_g}
                onChange={setField("serving_size_g")}
                className="flex-1 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                data-testid="contribute-input-serving"
              />
              <span className="text-sm text-neutral-500">
                {form.is_liquid ? (isEn ? "ml" : "مل") : (isEn ? "g" : "غ")}
              </span>
            </div>
          </div>

        </div>
      )}

      {step === "review" && (
        <footer className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-neutral-100 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            onClick={() => save()}
            className="w-full orange-gradient text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] shadow-brand"
            data-testid="contribute-save-btn"
          >
            <Check className="w-5 h-5" />
            {isEn ? "Save" : "احفظ"}
          </button>
        </footer>
      )}
    </div>
  );
}
